import * as microsoftTeams from '@microsoft/teams-js';

export const TEAMS_AUTH_START_PATH = '/teams-auth-start';

/** Set in appState so onRedirectCallback can close the Teams auth popup. */
export const TEAMS_AUTH_POPUP_STATE_KEY = 'teamsAuthPopup' as const;

/** Survives the Auth0 redirect when appState is unavailable. */
export const TEAMS_AUTH_POPUP_SESSION_KEY = 'teamsAuthPopupSession';

export type TeamsAuthAppState = {
  [TEAMS_AUTH_POPUP_STATE_KEY]?: boolean;
  returnTo?: string;
};

export function markTeamsAuthPopupSession(): void {
  sessionStorage.setItem(TEAMS_AUTH_POPUP_SESSION_KEY, '1');
}

export function clearTeamsAuthPopupSession(): void {
  sessionStorage.removeItem(TEAMS_AUTH_POPUP_SESSION_KEY);
}

export function isTeamsAuthPopupSession(): boolean {
  return sessionStorage.getItem(TEAMS_AUTH_POPUP_SESSION_KEY) === '1';
}

export function isTeamsAuthPopupFlow(): boolean {
  return (
    window.location.pathname.includes(TEAMS_AUTH_START_PATH) ||
    isTeamsAuthPopupSession()
  );
}

/** Opens Auth0 login in a Teams-managed popup (required inside Teams iframes). */
export async function authenticateWithTeams(options?: {
  organization?: string | null;
}): Promise<void> {
  await microsoftTeams.app.initialize();

  const url = new URL(`${window.location.origin}${TEAMS_AUTH_START_PATH}`);
  const organization = options?.organization?.trim();
  if (organization) {
    url.searchParams.set('org', organization);
  }

  await microsoftTeams.authentication.authenticate({
    url: url.toString(),
    width: 600,
    height: 535,
  });
}

let teamsAuthNotified = false;

export function notifyTeamsAuthSuccess(): void {
  if (teamsAuthNotified) return;
  teamsAuthNotified = true;
  clearTeamsAuthPopupSession();

  const notify = () => {
    microsoftTeams.authentication.notifySuccess('');
  };

  microsoftTeams.app
    .initialize()
    .then(() => {
      // Teams desktop can miss an immediate notifySuccess after Auth0 redirect.
      window.setTimeout(notify, 250);
    })
    .catch((error) => {
      console.warn('Teams notifySuccess failed, closing popup', error);
      teamsAuthNotified = false;
      window.close();
    });
}

export function notifyTeamsAuthFailure(message: string): void {
  if (teamsAuthNotified) return;
  teamsAuthNotified = true;
  clearTeamsAuthPopupSession();

  microsoftTeams.app
    .initialize()
    .then(() => {
      microsoftTeams.authentication.notifyFailure(message);
    })
    .catch((error) => {
      console.warn('Teams notifyFailure failed, closing popup', error);
      window.close();
    });
}
